import { SkipToContent } from "#/components/layout/SkipToContent.js";
import { createT } from "#/lib/i18n.js";

type Props = { locale: string };

const LAST_REVIEWED_BY_LOCALE: Record<string, string> = {
  en: "10 May 2026",
  ar: "10 مايو 2026",
  fr: "10 mai 2026",
};

export { Page };

function Page({ locale }: Props) {
  const t = createT(locale, "accessibility");
  const lastReviewed =
    LAST_REVIEWED_BY_LOCALE[locale] ?? LAST_REVIEWED_BY_LOCALE.en;

  return (
    <>
      <SkipToContent t={t} />
      <main
        id="main-content"
        tabIndex={-1}
        className="mx-auto max-w-3xl px-6 py-12"
      >
        <header className="mb-10">
          <h1 className="text-3xl font-semibold leading-tight">
            {t("statement.pageTitle")}
          </h1>
          <p className="mt-2 text-sm text-[color:var(--color-fg-muted)]">
            {t("statement.lastReviewed", { date: lastReviewed })}
          </p>
        </header>

        <p className="mb-8 leading-relaxed">{t("statement.intro")}</p>

        <section aria-labelledby="conformance-heading" className="mb-10">
          <h2 id="conformance-heading" className="text-xl font-semibold mb-3">
            {t("statement.conformance.heading")}
          </h2>
          <p className="mb-3 leading-relaxed">
            {t("statement.conformance.body")}
          </p>
          <p className="leading-relaxed text-[color:var(--color-fg-muted)]">
            {t("statement.conformance.scope")}
          </p>
        </section>

        <section aria-labelledby="known-issues-heading" className="mb-10">
          <h2 id="known-issues-heading" className="text-xl font-semibold mb-3">
            {t("statement.knownIssues.heading")}
          </h2>
          <p className="mb-3 leading-relaxed">
            {t("statement.knownIssues.body")}
          </p>
          <ul className="list-disc ps-6 space-y-2">
            {[0, 1].map((i) => {
              const title = t(`statement.knownIssues.items.${i}.title`);
              const target = t(`statement.knownIssues.items.${i}.target`);
              if (title.startsWith("statement.knownIssues.")) return null;
              return (
                <li key={i}>
                  <span className="font-medium">{title}</span>
                  <span className="block text-sm text-[color:var(--color-fg-muted)]">
                    {target}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        <section aria-labelledby="feedback-heading" className="mb-10">
          <h2 id="feedback-heading" className="text-xl font-semibold mb-3">
            {t("statement.feedback.heading")}
          </h2>
          <p className="mb-3 leading-relaxed">
            {t("statement.feedback.body")}
          </p>
          <p className="leading-relaxed">
            <span className="font-medium">
              {t("statement.feedback.emailLabel")}:{" "}
            </span>
            <a
              href={`mailto:${t("statement.feedback.email")}`}
              className="underline"
            >
              {t("statement.feedback.email")}
            </a>
          </p>
          <p className="text-sm text-[color:var(--color-fg-muted)] mt-2">
            {t("statement.feedback.responseTime")}
          </p>
        </section>

        <section aria-labelledby="enforcement-heading" className="mb-10">
          <h2 id="enforcement-heading" className="text-xl font-semibold mb-3">
            {t("statement.enforcement.heading")}
          </h2>
          <p className="mb-3 leading-relaxed">
            {t("statement.enforcement.body")}
          </p>
          <p>
            <a
              href={t("statement.enforcement.ksaDgaUrl")}
              rel="external noopener"
              className="underline"
            >
              {t("statement.enforcement.ksaDgaLabel")}
            </a>
          </p>
        </section>

        <section aria-labelledby="tech-stack-heading">
          <h2 id="tech-stack-heading" className="text-xl font-semibold mb-3">
            {t("statement.techStack.heading")}
          </h2>
          <ul className="list-disc ps-6 space-y-1">
            {[0, 1, 2, 3, 4].map((i) => {
              const item = t(`statement.techStack.items.${i}`);
              if (item.startsWith("statement.techStack.")) return null;
              return <li key={i}>{item}</li>;
            })}
          </ul>
        </section>
      </main>
    </>
  );
}
